import { sanitizeCssColor, sanitizeFontFamily } from './sanitize.js';
import { onAccentColours, tintOf, toHex } from './design-extras.js';
export const defaultTheme = {
    name: 'Paper & Ink',
    palette: {
        background: '#F3EFE4',
        surface: '#FFFFFF',
        text: '#0F1A2E',
        heading: '#0F1A2E',
        accent: '#13295E',
        accentText: '#FFFFFF',
        muted: '#5B594F',
        border: '#E4DCCB'
    },
    fonts: {
        heading: '"Archivo", sans-serif',
        body: '"Archivo", sans-serif',
        mono: '"IBM Plex Mono", monospace'
    },
    radius: {
        sm: '0.375rem',
        md: '0.75rem',
        lg: '1.25rem'
    },
    spacing: {
        section: '4.5rem',
        container: '72rem'
    }
};
export function isCssLength(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    if (trimmed === '')
        return false;
    return /^[0-9]+(?:\.[0-9]+)?(?:rem|em|px|%)?$/.test(trimmed);
}
export function themeToCss(theme) {
    const color = (value) => sanitizeCssColor(value) ?? '#FFFFFF';
    const font = (value) => sanitizeFontFamily(value) || 'sans-serif';
    // Section styles (tint / bold brand colour) need colours that are guaranteed readable on top of them.
    const bgHex = toHex(color(theme.palette.background));
    const accentHex = toHex(color(theme.palette.accent));
    const accentTextHex = toHex(color(theme.palette.accentText));
    const derived = bgHex && accentHex && accentTextHex
        ? (() => {
            const on = onAccentColours(accentHex, accentTextHex);
            return `  --site-tint: ${tintOf(bgHex, accentHex)};\n` +
                `  --site-on-accent-muted: ${on.muted};\n` +
                `  --site-on-accent-surface: ${on.surface};\n` +
                `  --site-on-accent-border: ${on.border};\n`;
        })()
        : '';
    const size = (value, fallback) => isCssLength(value) ? value : fallback;
    return `:root {\n` +
        `  --site-background: ${color(theme.palette.background)};\n` +
        `  --site-surface: ${color(theme.palette.surface)};\n` +
        `  --site-text: ${color(theme.palette.text)};\n` +
        `  --site-heading: ${color(theme.palette.heading)};\n` +
        `  --site-accent: ${color(theme.palette.accent)};\n` +
        `  --site-accentText: ${color(theme.palette.accentText)};\n` +
        `  --site-accent-base: ${color(theme.palette.accent)};\n` +
        `  --site-accentText-base: ${color(theme.palette.accentText)};\n` +
        derived +
        `  --site-muted: ${color(theme.palette.muted)};\n` +
        `  --site-border: ${color(theme.palette.border)};\n` +
        `  --site-heading-font: ${font(theme.fonts.heading)};\n` +
        `  --site-body-font: ${font(theme.fonts.body)};\n` +
        `  --site-mono-font: ${font(theme.fonts.mono)};\n` +
        `  --site-radius-sm: ${size(theme.radius.sm, '0.375rem')};\n` +
        `  --site-radius-md: ${size(theme.radius.md, '0.75rem')};\n` +
        `  --site-radius-lg: ${size(theme.radius.lg, '1rem')};\n` +
        `  --site-section-space: ${size(theme.spacing.section, '4rem')};\n` +
        `  --site-container: ${size(theme.spacing.container, '72rem')};\n` +
        `}`;
}
//# sourceMappingURL=theme.js.map